// Angular
import {computed, Injectable, signal} from '@angular/core';

// App
import {
  REGISTRATION_ROUTE_SECTION_STEP,
  RegistrationStepRoute,
} from '@app/features/registration/constants/registration-steps.const';
import {
  RegistrationFieldId,
  RegistrationFieldStatus,
  RegistrationSectionStatus,
  RegistrationStatus,
} from '@app/features/registration/enums';
import type {
  RegistrationField,
  RegistrationResult,
  RegistrationSection,
} from '@app/features/registration/interfaces';

@Injectable()
export class RegistrationStoreService {
  readonly registration = signal<RegistrationResult | null>(null);
  readonly loading = signal(false);
  readonly submitting = signal(false);

  readonly isVerified = computed(() => this.registration()?.isVerified ?? false);
  readonly status = computed(() => this.registration()?.status ?? null);
  readonly sections = computed(() => this.registration()?.sections ?? []);

  private readonly fieldMap = computed(() => {
    const map = new Map<RegistrationFieldId, RegistrationField>();

    for (const section of this.sections()) {
      for (const field of section.fields) {
        map.set(field.id, field);
      }
    }

    return map;
  });

  private readonly rejectedReasonMap = computed(() => {
    const map = new Map<RegistrationFieldId, string>();

    for (const section of this.sections()) {
      for (const field of section.fields) {
        const reason = field.rejectDescription?.trim();

        if (field.status === RegistrationFieldStatus.Rejected && reason) {
          map.set(field.id, reason);
        }
      }
    }

    return map;
  });

  setRegistration(result: RegistrationResult | null): void {
    this.registration.set(result);
  }

  setLoading(value: boolean): void {
    this.loading.set(value);
  }

  setSubmitting(value: boolean): void {
    this.submitting.set(value);
  }

  reset(): void {
    this.registration.set(null);
    this.loading.set(false);
    this.submitting.set(false);
  }

  getField(fieldId: RegistrationFieldId): RegistrationField | undefined {
    return this.fieldMap().get(fieldId);
  }

  getFieldValue(fieldId: RegistrationFieldId): string | null {
    return this.getField(fieldId)?.value ?? null;
  }

  getFieldRejectDescription(fieldId: RegistrationFieldId): string | null {
    return this.rejectedReasonMap().get(fieldId) ?? null;
  }

  isFieldEditable(fieldId: RegistrationFieldId): boolean {
    const status = this.getField(fieldId)?.status;

    if (status == null) {
      return true;
    }

    return (
      status === RegistrationFieldStatus.Empty
      || status === RegistrationFieldStatus.PendingByUser
      || status === RegistrationFieldStatus.Rejected
    );
  }

  isFieldVerified(fieldId: RegistrationFieldId): boolean {
    return this.getField(fieldId)?.status === RegistrationFieldStatus.Verified;
  }

  isFieldControlDisabled(fieldId: RegistrationFieldId): boolean {
    if (fieldId === RegistrationFieldId.IsLegal) {
      if (this.isPendingByAdmin()) {
        return true;
      }

      return !this.isFieldEditable(fieldId) && !this.isFieldVerified(fieldId);
    }

    return !this.isFieldEditable(fieldId);
  }

  getSection(step: number): RegistrationSection | undefined {
    return this.sections().find(section => section.step === step);
  }

  getSectionByRoute(route: RegistrationStepRoute): RegistrationSection | undefined {
    return this.getSection(REGISTRATION_ROUTE_SECTION_STEP[route]);
  }

  isSectionNavigable(status: RegistrationSectionStatus): boolean {
    return (
      status !== RegistrationSectionStatus.Inactive
      && status !== RegistrationSectionStatus.PendingByAdmin
    );
  }

  isSectionFinished(route: RegistrationStepRoute): boolean {
    return this.getSectionByRoute(route)?.status === RegistrationSectionStatus.Finished;
  }

  canAdvanceFromSection(route: RegistrationStepRoute): boolean {
    return this.getSectionByRoute(route)?.status !== RegistrationSectionStatus.Inactive;
  }

  canAdvanceWithoutSubmit(route: RegistrationStepRoute): boolean {
    const status = this.getSectionByRoute(route)?.status;

    return status === RegistrationSectionStatus.Finished
      || status === RegistrationSectionStatus.PendingByAdmin;
  }

  isPendingByAdmin(): boolean {
    return this.status() === RegistrationStatus.PendingByAdmin;
  }

  isFinished(): boolean {
    return this.status() === RegistrationStatus.Finished;
  }
}
